/**
 * EditSection — a section that reads as a record by default and flips to a
 * form on Edit, with a Save/Cancel footer (port of the design's NBEditSection).
 * Presentational: the parent owns the form state and supplies `onSave`
 * (awaited; stays in edit mode if it rejects) and `onCancel` (resets the
 * form). When `readOnly` the Edit affordance is gone and any open form closes.
 */
import { useId, useState, type ReactNode } from 'react';

import { EditSectionFooter } from './EditSectionFooter';
import { EditSectionHeader } from './EditSectionHeader';
import { useEditSectionFocus } from './use-edit-section-focus';
import { VisuallyHiddenStatus } from './VisuallyHiddenStatus';

export interface EditSectionProps {
  title: string;
  hint?: string;
  /** Extra header control, left of the Edit button. */
  action?: ReactNode;
  /** Drop the top margin (first section in a card). */
  first?: boolean;
  /** False → section has never been filled; starts open with a Save-only footer. */
  complete?: boolean;
  saveLabel?: string;
  /** Accessible name for the Edit button, e.g. "Edit When & what". */
  editLabel?: string;
  /** Replaces the footer reminder. Null shows none. */
  footerNote?: string | null;
  /** Hide editing entirely (RD read-only scope / no write access). */
  readOnly?: boolean;
  /** A save was refused for lack of access: keep the typed form open, drop Save. */
  frozen?: boolean;
  view: ReactNode;
  form: ReactNode;
  onSave: () => Promise<void>;
  onCancel?: () => void;
  /** Called as the form opens, so the parent can seed it from fresh data. */
  onEdit?: () => void;
}

export function EditSection(props: EditSectionProps) {
  const { title, hint, action, first, complete = true, saveLabel, readOnly, view, form } = props;
  const [editing, setEditing] = useState(!complete);
  const [saved, setSaved] = useState(complete);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const headingId = useId();
  const showForm = editing && !readOnly;
  const canSave = !props.frozen;
  const { formRef, editRef } = useEditSectionFocus(showForm, failures);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await props.onSave();
      setEditing(false);
      setSaved(true);
      setAnnouncement('Saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save — try again.');
      setFailures((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };
  const cancel = () => {
    if (busy) return;
    props.onCancel?.();
    setError(null);
    setEditing(false);
  };

  return (
    <section aria-labelledby={headingId} style={{ margin: `${first ? 0 : 22}px 0 0` }}>
      <EditSectionHeader
        id={headingId}
        title={title}
        hint={hint}
        action={action}
        unsaved={!saved}
        canEdit={!showForm && !readOnly && !props.frozen}
        editLabel={props.editLabel}
        editRef={editRef}
        onEdit={() => {
          setAnnouncement('');
          props.onEdit?.();
          setEditing(true);
        }}
      />

      <div ref={formRef} style={{ paddingTop: 14 }}>
        {showForm ? form : view}
      </div>

      {showForm && (
        <EditSectionFooter
          saved={saved}
          busy={busy}
          canSave={canSave}
          saveLabel={saveLabel}
          error={error}
          note={props.footerNote}
          onCancel={cancel}
          onSave={() => void save()}
        />
      )}
      <VisuallyHiddenStatus message={announcement} />
    </section>
  );
}
